import { useMemo } from 'react';
import parse from 'autosuggest-highlight/parse';

import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

interface Props {
  text: string;
  /** Terms to highlight. When empty the text renders unchanged. */
  terms?: string[];
}

/** Merge possibly-overlapping/unsorted [start, end] ranges so `parse` gets clean, ordered chunks. */
const mergeRanges = (ranges: [number, number][]): [number, number][] => {
  if (ranges.length <= 1) return ranges;
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const [start, end] = sorted[i];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Every case-insensitive occurrence of a term, including inside a longer word — the backend matches
 * with `LIKE '%term%'`, so "react" matched "ReactJS" and should be marked there too. The term is
 * matched as written: a phrase stays a phrase. Splitting into words is the caller's decision, made
 * from how the search was compiled (JT1-1693, `src/utils/highlight-terms.ts`).
 */
const findRanges = (text: string, term: string): [number, number][] =>
  Array.from(text.matchAll(new RegExp(escapeRegExp(term), 'gi')), (hit) => [hit.index ?? 0, (hit.index ?? 0) + hit[0].length]);

/**
 * Renders text with search terms visually highlighted (spec items 14/18, JT1-1537; JT1-1693), wrapping
 * each hit in a theme-aware <mark>. Shared by the resume, the candidate list cells and the record panel
 * so a search highlights identically everywhere. With no terms it renders the text unchanged.
 */
export default function HighlightedText({ text, terms }: Props) {
  // Memoised because a long resume with every occurrence matched is not free to re-scan on each render.
  const parts = useMemo(() => {
    const active = (terms ?? []).map((term) => term.trim()).filter(Boolean);
    if (!active.length || !text) return null;

    // Merging keeps overlapping hits from different terms as a single mark.
    const ranges = mergeRanges(active.flatMap((term) => findRanges(text, term)));
    if (!ranges.length) return null;
    return parse(text, ranges);
  }, [text, terms]);

  if (!parts) return <>{text}</>;

  return (
    <>
      {parts.map((part, index) =>
        part.highlight ? (
          // eslint-disable-next-line react/no-array-index-key
          <Box
            key={index}
            component="mark"
            sx={{
              px: 0.25,
              borderRadius: 0.5,
              color: 'inherit',
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.4),
            }}
          >
            {part.text}
          </Box>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part.text}</span>
        )
      )}
    </>
  );
}
